// ============================================================
// src/App.js
// ============================================================

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth }             from './hooks/useAuth'
import { MusicPlayerProvider } from './context/MusicPlayerContext'
import { PendingRequestsProvider } from './context/PendingRequestsContext'
import MiniPlayer    from './components/MiniPlayer'
import Layout        from './components/layout'
import Login         from './pages/login'
import Dashboard     from './pages/dashboard'
import { GigList, GigDetails, AddGig } from './pages/gig'
import Calendar      from './pages/calendar'
import Contacts      from './pages/contacts'
import { PhotoLibrary, MusicLibrary } from './pages/library'
import Settings      from './pages/settings'
import Travel        from './pages/travel'
import Earnings      from './pages/earnings'
import PromoGenerator from './pages/promo'
import About         from './pages/about'
import PrivacyPolicy from './pages/privacypolicy'
import ShowRequests  from './pages/ShowRequests'
import Links, { PublicLinks } from './pages/links'

function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-4xl)', color: 'var(--brand-primary)',
      }}>
        Loading...
      </div>
    )
  }

  // -------------------------------------------------------
  // Single BrowserRouter wrapping everything. Public routes
  // (fan links, about, privacy) are declared FIRST so they
  // always match regardless of auth state -- a logged-in
  // performer visiting /links/kingperk no longer gets bounced
  // to the dashboard. Protected routes are handled below via
  // the user guard.
  // -------------------------------------------------------
  return (
    <BrowserRouter>
      <Routes>

        {/* ── Always-public routes (no auth required, no Layout) ── */}
        <Route path="/fanlinks/:performerId" element={<PublicLinks />} />
        <Route path="/about"             element={<About />} />
        <Route path="/privacy"           element={<PrivacyPolicy />} />

        {/* ── Auth-gated routes ── */}
        <Route
          path="*"
          element={
            !user ? (
              // Not logged in: show login for everything else
              <Login />
            ) : (
              // Logged in: wrap protected pages in providers + Layout
              <MusicPlayerProvider>
                {/* PendingRequestsProvider fetches live pending counts once
                    and makes them available to Layout via usePendingRequests().
                    This drives the badge on the Requests nav item. */}
                <PendingRequestsProvider>
                  <Layout>
                    <Routes>
                      <Route path="/"              element={<Dashboard />} />
                      <Route path="/gigs"          element={<GigList />} />
                      <Route path="/gigs/new"      element={<AddGig />} />
                      <Route path="/gigs/:id"      element={<GigDetails />} />
                      <Route path="/gigs/edit/:id" element={<AddGig />} />
                      <Route path="/calendar"      element={<Calendar />} />
                      <Route path="/contacts"      element={<Contacts />} />
                      <Route path="/photos"        element={<PhotoLibrary />} />
                      <Route path="/music"         element={<MusicLibrary />} />
                      <Route path="/settings"      element={<Settings />} />
                      <Route path="/travel"        element={<Travel />} />
                      <Route path="/earnings"      element={<Earnings />} />
                      <Route path="/promo"         element={<PromoGenerator />} />
                      <Route path="/requests"      element={<ShowRequests />} />
                      <Route path="/links"         element={<Links />} />
                      <Route path="*"              element={<Navigate to="/" />} />
                    </Routes>
                  </Layout>
                  <MiniPlayer />
                </PendingRequestsProvider>
              </MusicPlayerProvider>
            )
          }
        />

      </Routes>
    </BrowserRouter>
  )
}

export default App